import { useTranslate } from 'react-admin';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  Grid,
  Tooltip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export type SmtpEncryption = 'starttls' | 'ssl';

export interface SmtpFormData {
  smtpEnabled: boolean;
  smtpAddress: string;
  smtpPort: number;
  smtpLogin: string;
  smtpPassword: string;
  smtpSelfSignedCert: boolean;
}

export const defaultSmtpData: SmtpFormData = {
  smtpEnabled: false,
  smtpAddress: '',
  smtpPort: 587,
  smtpLogin: '',
  smtpPassword: '',
  smtpSelfSignedCert: false,
};

/** Derive encryption mode from port number */
export function encryptionFromPort(port: number): SmtpEncryption {
  return port === 465 ? 'ssl' : 'starttls';
}

interface SmtpFormFieldsProps {
  data: SmtpFormData;
  onChange: <K extends keyof SmtpFormData>(key: K, value: SmtpFormData[K]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  onClearError?: (field: string) => void;
}

export function SmtpFormFields({ data, onChange, disabled, errors = {}, onClearError }: SmtpFormFieldsProps) {
  const translate = useTranslate();

  const handleChange = <K extends keyof SmtpFormData>(key: K, value: SmtpFormData[K]) => {
    onChange(key, value);
    onClearError?.(key);
  };

  const encryption = encryptionFromPort(data.smtpPort);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.smtpAddress')}
          value={data.smtpAddress}
          onChange={(e) => handleChange('smtpAddress', e.target.value)}
          placeholder="smtp.gmail.com"
          required
          disabled={disabled}
          error={!!errors.smtpAddress}
          helperText={errors.smtpAddress}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.smtpPort')}
          value={data.smtpPort}
          onChange={(e) => handleChange('smtpPort', parseInt(e.target.value) || 587)}
          type="number"
          required
          disabled={disabled}
          error={!!errors.smtpPort}
          helperText={
            errors.smtpPort ||
            (encryption === 'ssl'
              ? translate('resources.channel.wizard.email.sslHelp')
              : translate('resources.channel.wizard.email.starttlsHelp'))
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.smtpLogin')}
          value={data.smtpLogin}
          onChange={(e) => handleChange('smtpLogin', e.target.value)}
          required
          disabled={disabled}
          error={!!errors.smtpLogin}
          helperText={errors.smtpLogin || translate('resources.channel.wizard.email.usernameHelper')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.smtpPassword')}
          value={data.smtpPassword}
          onChange={(e) => handleChange('smtpPassword', e.target.value)}
          type="password"
          required
          disabled={disabled}
          error={!!errors.smtpPassword}
          helperText={errors.smtpPassword || translate('resources.channel.wizard.email.passwordHelper')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={translate('resources.channel.wizard.email.appPasswordTooltip')}
                  arrow
                  placement="top"
                >
                  <IconButton size="small" tabIndex={-1} edge="end">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
          {translate('resources.channel.wizard.email.securitySettings')}
        </Typography>
      </Grid>

      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.smtpSelfSignedCert}
              onChange={(e) => handleChange('smtpSelfSignedCert', e.target.checked)}
              disabled={disabled}
            />
          }
          label={translate('resources.channel.wizard.email.smtpSelfSignedCert')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {translate('resources.channel.wizard.email.smtpSelfSignedCertHelp')}
        </Typography>
      </Grid>
    </Grid>
  );
}
